import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Snackbar, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme, useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoneyOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import api from '../api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CostTracking = ({ requestId, onCostUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addCostDialog, setAddCostDialog] = useState(false);
  const [editCostDialog, setEditCostDialog] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [newCost, setNewCost] = useState({
    description: '',
    amount: '',
    category: 'labor',
    date: new Date().toISOString().split('T')[0],
    contractorId: '',
    invoiceNumber: '',
    notes: ''
  });

  const costCategories = [
    { value: 'labor', label: 'Labor', color: 'primary' },
    { value: 'materials', label: 'Materials', color: 'secondary' },
    { value: 'equipment', label: 'Equipment', color: 'success' },
    { value: 'permits', label: 'Permits', color: 'warning' },
    { value: 'other', label: 'Other', color: 'default' }
  ];

  useEffect(() => {
    if (requestId) {
      fetchCosts();
    }
  }, [requestId]);

  const fetchCosts = async () => {
    try {
      console.log('Fetching costs for requestId:', requestId);
      const response = await api.get(`/api/maintenance-requests/${requestId}/costs`);
      console.log('Costs response:', response.data);
      setCosts(response.data);
    } catch (error) {
      console.error('Error fetching costs:', error);
      setSnackbar({ open: true, message: `Failed to fetch costs: ${error.response?.data?.message || error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = async () => {
    try {
      const costData = {
        ...newCost,
        amount: parseFloat(newCost.amount) || 0,
        requestId
      };

      console.log('Adding cost:', costData);
      const response = await api.post(`/api/maintenance-requests/${requestId}/costs`, costData);
      console.log('Cost added successfully:', response.data);
      setSnackbar({ open: true, message: 'Cost added successfully!', severity: 'success' });
      setAddCostDialog(false);
      setNewCost({
        description: '',
        amount: '',
        category: 'labor',
        date: new Date().toISOString().split('T')[0],
        contractorId: '',
        invoiceNumber: '',
        notes: ''
      });
      fetchCosts();
      if (onCostUpdate) onCostUpdate();
    } catch (error) {
      console.error('Error adding cost:', error);
      setSnackbar({ open: true, message: `Failed to add cost: ${error.response?.data?.message || error.message}`, severity: 'error' });
    }
  };

  const handleEditCost = async () => {
    try {
      const costData = {
        ...selectedCost,
        amount: parseFloat(selectedCost.amount) || 0
      };

      await api.put(`/api/maintenance-requests/${requestId}/costs/${selectedCost.id}`, costData);
      setSnackbar({ open: true, message: 'Cost updated successfully!', severity: 'success' });
      setEditCostDialog(false);
      setSelectedCost(null);
      fetchCosts();
      if (onCostUpdate) onCostUpdate();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update cost', severity: 'error' });
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this cost entry?')) return;
    try {
      await api.delete(`/api/maintenance-requests/${requestId}/costs/${costId}`);
      setSnackbar({ open: true, message: 'Cost deleted successfully!', severity: 'success' });
      fetchCosts();
      if (onCostUpdate) onCostUpdate();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete cost', severity: 'error' });
    }
  };

  const handleEdit = (cost) => {
    setSelectedCost(cost);
    setEditCostDialog(true);
  };

  const getTotalCost = () => {
    return costs.reduce((total, cost) => total + (parseFloat(cost.amount) || 0), 0);
  };

  const getCostByCategory = (category) => {
    return costs
      .filter(cost => cost.category === category)
      .reduce((total, cost) => total + (parseFloat(cost.amount) || 0), 0);
  };

  const getCategoryColor = (category) => {
    const cat = costCategories.find(c => c.value === category);
    return cat ? cat.color : 'default';
  };

  const getCategoryLabel = (category) => {
    const cat = costCategories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading costs...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1, sm: 0 } }}
        >
          <AttachMoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cost Tracking
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddCostDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Add' : 'Add Cost'}
        </Button>
      </Box>

      {/* Cost Summary Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="primary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Total Cost
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                ${getTotalCost().toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Labor
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                ${getCostByCategory('labor').toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="success.main"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Materials
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                ${getCostByCategory('materials').toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cost Breakdown by Category */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Cost Breakdown
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {costCategories.map((category) => (
              <Grid item xs={6} sm={4} md={3} key={category.value}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: { xs: 1.5, sm: 2 }, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1 
                }}>
                  <Typography 
                    variant="subtitle2" 
                    color={`${category.color}.main`}
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {category.label}
                  </Typography>
                  <Typography 
                    variant="h6"
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    ${getCostByCategory(category.value).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Cost Details Table */}
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Cost Details
          </Typography>
          <TableContainer 
            component={Paper}
            sx={{ 
              overflowX: 'auto',
              maxWidth: { xs: '100%', sm: 'none' }
            }}
          >
            <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Date</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Description</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Invoice</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costs.map((cost) => (
                  <TableRow key={cost.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {new Date(cost.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {cost.description}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getCategoryLabel(cost.category)} 
                        color={getCategoryColor(cost.category)} 
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      ${parseFloat(cost.amount).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {cost.invoiceNumber || '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                        <Tooltip title="Edit Cost">
                          <IconButton 
                            size={isMobile ? 'small' : 'medium'} 
                            onClick={() => handleEdit(cost)}
                            aria-label="Edit cost"
                          >
                            <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Cost">
                          <IconButton 
                            size={isMobile ? 'small' : 'medium'} 
                            color="error" 
                            onClick={() => handleDeleteCost(cost.id)}
                            aria-label="Delete cost"
                          >
                            <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {costs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No costs recorded yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Cost Dialog */}
      <Dialog 
        open={addCostDialog} 
        onClose={() => setAddCostDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add Cost Entry
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Description"
            value={newCost.description}
            onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Amount"
            value={newCost.amount}
            onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
            fullWidth
            margin="dense"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={newCost.category}
              label="Category"
              onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
            >
              {costCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Date"
            value={newCost.date}
            onChange={(e) => setNewCost({ ...newCost, date: e.target.value })}
            fullWidth
            margin="dense"
            type="date"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Invoice Number"
            value={newCost.invoiceNumber}
            onChange={(e) => setNewCost({ ...newCost, invoiceNumber: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Notes"
            value={newCost.notes}
            onChange={(e) => setNewCost({ ...newCost, notes: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddCostDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCost} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Cost'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cost Dialog */}
      <Dialog 
        open={editCostDialog} 
        onClose={() => setEditCostDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Cost Entry
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {selectedCost && (
            <>
              <TextField
                label="Description"
                value={selectedCost.description}
                onChange={(e) => setSelectedCost({ ...selectedCost, description: e.target.value })}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Amount"
                value={selectedCost.amount}
                onChange={(e) => setSelectedCost({ ...selectedCost, amount: e.target.value })}
                fullWidth
                margin="dense"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCost.category}
                  label="Category"
                  onChange={(e) => setSelectedCost({ ...selectedCost, category: e.target.value })}
                >
                  {costCategories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Date"
                value={selectedCost.date}
                onChange={(e) => setSelectedCost({ ...selectedCost, date: e.target.value })}
                fullWidth
                margin="dense"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Invoice Number"
                value={selectedCost.invoiceNumber}
                onChange={(e) => setSelectedCost({ ...selectedCost, invoiceNumber: e.target.value })}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Notes"
                value={selectedCost.notes}
                onChange={(e) => setSelectedCost({ ...selectedCost, notes: e.target.value })}
                fullWidth
                margin="dense"
                multiline
                rows={2}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditCostDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditCost} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Update' : 'Update Cost'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CostTracking; 